import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { ListStyled, StyledListItemButton, StyledListItemIcon, AnimatedCollapse } from './styled';
import { SidebarContext, SidebarNavItemCollapseContext } from '../../context';
import { memo, useContext, useEffect } from 'react';

import Box from '@mui/material/Box';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ListItemText from '@mui/material/ListItemText';
import { NavItemType } from '../../type';
import SidebarNavItem from '../SidebarNavItem/SidebarNavItem';
import { SidebarNavItemCollapseProps } from '../../props';
import { Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useQueryState } from 'nuqs';
import { useNavigate } from 'react-router-dom';
import tid from '@/common/infrastructure/config/tid';

const Menus = memo(({ menu, level, parentId }: SidebarNavItemCollapseProps) => {
  return menu.children?.map((item) => {
    switch (item.type) {
      case 'collapse':
        return <SidebarNavItemCollapse key={item.id} menu={item} level={level + 1} parentId={parentId} />;
      case 'item':
        return <SidebarNavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });
});

const MenuIcon = memo(
  ({ menu, activeModule, sidebarOpen, level }: { menu: NavItemType; activeModule: boolean; sidebarOpen: boolean; level: number }) => {
    const Icon = menu.icon!;
    const IconSelected = menu.iconSelected!;
    const iconSelectedColor = 'white';

    if (menu.iconSelected && activeModule) {
      return <IconSelected strokeWidth={1.5} size={sidebarOpen ? '10px`' : '24px'} sx={{ color: iconSelectedColor }} />;
    }

    if (menu.icon) {
      return <Icon strokeWidth={1.5} size={sidebarOpen ? '10px`' : '24px'} sx={{ color: iconSelectedColor }} />;
    }

    return (
      <FiberManualRecordIcon
        sx={{ width: activeModule ? 8 : 6, height: activeModule ? 8 : 6 }}
        fontSize={level > 0 ? 'inherit' : 'medium'}
      />
    );
  }
);

const SidebarNavItemCollapse = ({ menu, level, parentId }: SidebarNavItemCollapseProps) => {
  const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);
  const { currentMenuActive, setCurrentMenuActive } = useContext(SidebarNavItemCollapseContext);
  const [moduleParam] = useQueryState('module');
  const [subModuleParam] = useQueryState('submodule');
  const navigate = useNavigate();

  useEffect(() => {
    if (moduleParam === menu.id || subModuleParam === menu.id) {
      setCurrentMenuActive(menu.id ?? null);
    }
  }, [moduleParam, subModuleParam, menu.id, setCurrentMenuActive]);

  const iconSelectedColor = 'white';
  const routeActive = moduleParam === menu.id || subModuleParam === menu.id;
  const isExpanded = currentMenuActive === menu.id;

  const showChevron = menu.showChevron ?? true;

  return (
    <>
      <Tooltip title={!sidebarOpen ? menu.title : ''} placement="right" arrow>
        <li role="none">
          <StyledListItemButton
            role="menuitem"
            level={level}
            sidebarOpen={sidebarOpen}
            iconSelectedColor={iconSelectedColor}
            selected={routeActive || isExpanded}
            onClick={() => {
              if (!showChevron && menu.url) {
                navigate(menu.url);
              }

              setSidebarOpen(true);
              setCurrentMenuActive(isExpanded ? null : (menu.id ?? null));
            }}
            {...tid(`common:layout:sidebar-nav-item-collapse:button-${menu.id}`)}
          >
            <StyledListItemIcon
              sx={{ border: (routeActive || isExpanded) && !sidebarOpen ? '1px solid white' : 'none' }}
              level={level}
              sidebarOpen={sidebarOpen}
              activeModule={routeActive || isExpanded}
              iconSelectedColor={iconSelectedColor}
            >
              <MenuIcon menu={menu} activeModule={routeActive || isExpanded} sidebarOpen={sidebarOpen} level={level} />
            </StyledListItemIcon>
            {(sidebarOpen || (!sidebarOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Typography
                    noWrap
                    overflow="hidden"
                    textOverflow="ellipsis"
                    variant={'body1'}
                    sx={{ color: iconSelectedColor }}
                    width={130}
                    fontWeight={routeActive || isExpanded ? 'bold' : 'normal'}
                  >
                    {menu.title ? (menu.title as string).toUpperCase() : ''}
                  </Typography>
                }
                secondary={
                  menu.caption && (
                    <Typography variant="caption" sx={{ color: iconSelectedColor }} display="block" gutterBottom>
                      {menu.caption}
                    </Typography>
                  )
                }
              />
            )}
            {sidebarOpen && showChevron && (
              <Box>
                {isExpanded ? (
                  <IconChevronUp stroke={1.5} size="16px" style={{ color: 'white', marginTop: 'auto', marginBottom: 'auto' }} />
                ) : (
                  <IconChevronDown stroke={1.5} size="16px" style={{ color: 'white', marginTop: 'auto', marginBottom: 'auto' }} />
                )}
              </Box>
            )}
          </StyledListItemButton>
        </li>
      </Tooltip>
      {sidebarOpen && (
        <AnimatedCollapse className={isExpanded ? 'expanded' : ''}>
          {isExpanded && (
            <ListStyled role="menu" disablePadding>
              <Menus menu={menu} level={level} parentId={parentId} />
            </ListStyled>
          )}
        </AnimatedCollapse>
      )}
    </>
  );
};

export default memo(SidebarNavItemCollapse);
